import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileCode } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContractDraftForm = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('template_id');
  const [template, setTemplate] = useState(null);
  const [params, setParams] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!templateId) { navigate('/creator/contracts/templates'); return; }
    fetchTemplate();
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/contracts/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const tpl = res.data.template;
      setTemplate(tpl);
      const defaults = {};
      for (const [key, def] of Object.entries(tpl.fields_schema || {})) {
        if (def.default !== undefined) defaults[key] = def.default;
        else defaults[key] = '';
      }
      setParams(defaults);
    } catch (err) {
      console.error('Error loading template:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateParam = (key, value) => setParams(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/contracts/drafts`, {
        template_id: templateId,
        params
      }, { headers: { Authorization: `Bearer ${token}` } });
      navigate(`/creator/contracts/drafts/${res.data.draft_id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.validation_errors) {
        setErrors(detail.validation_errors);
      } else {
        setErrors([typeof detail === 'string' ? detail : 'Failed to create draft']);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyle = {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${theme.colors.borders}`,
    borderRadius: theme.radius.sm,
    fontSize: '14px',
    background: theme.colors.bg.card,
    color: theme.colors.text.primary,
    outline: 'none'
  };
  const labelStyle = { fontSize: '13px', fontWeight: 500, color: theme.colors.text.primary, display: 'block', marginBottom: '6px' };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.text.secondary }}>Loading template...</div>;
  }
  if (!template) {
    return <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.status.error }}>Template not found.</div>;
  }

  const schema = template.fields_schema || {};

  return (
    <div style={{ padding: '40px', maxWidth: '700px' }}>
      <button
        data-testid="back-to-templates"
        onClick={() => navigate('/creator/contracts/templates')}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 0',
          background: 'none', border: 'none', color: theme.colors.text.secondary,
          fontSize: '14px', cursor: 'pointer', marginBottom: '24px'
        }}
      >
        <ArrowLeft size={16} /> Back to Templates
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
        <div style={{
          width: '44px', height: '44px', borderRadius: '12px',
          background: `${theme.colors.neon.blue}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <FileCode size={22} style={{ color: theme.colors.neon.blue }} />
        </div>
        <div>
          <h1 data-testid="draft-form-title" style={{
            fontFamily: theme.fonts.heading, fontSize: '24px', fontWeight: 700,
            color: theme.colors.text.primary
          }}>
            {template.name}
          </h1>
          <p style={{ fontSize: '13px', color: theme.colors.text.secondary }}>
            {template.standard} &middot; v{template.version} &middot; Fill in parameters to generate your draft
          </p>
        </div>
      </div>

      {errors.length > 0 && (
        <div data-testid="draft-form-errors" style={{
          background: `${theme.colors.status.error}08`,
          border: `1px solid ${theme.colors.status.error}30`,
          borderRadius: theme.radius.sm,
          padding: '12px 16px',
          marginBottom: '20px'
        }}>
          {errors.map((err, i) => (
            <p key={i} style={{ fontSize: '13px', color: theme.colors.status.error, margin: '2px 0' }}>{err}</p>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{
          background: theme.colors.bg.card,
          border: `1px solid ${theme.colors.borders}`,
          borderRadius: theme.radius.card,
          padding: '28px',
          boxShadow: theme.colors.shadows.sm,
          display: 'flex', flexDirection: 'column', gap: '20px'
        }}>
          {Object.entries(schema).map(([key, def]) => (
            <div key={key}>
              <label style={labelStyle}>
                {def.label || key}{def.required ? ' *' : ''}
              </label>
              {def.description && (
                <p style={{ fontSize: '12px', color: theme.colors.text.muted, marginBottom: '6px' }}>{def.description}</p>
              )}
              {def.type === 'boolean' ? (
                <label data-testid={`field-${key}`} style={{
                  display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px',
                  color: theme.colors.text.primary
                }}>
                  <input
                    type="checkbox"
                    checked={params[key] === true || params[key] === 'true'}
                    onChange={(e) => updateParam(key, e.target.checked)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  {params[key] ? 'Enabled' : 'Disabled'}
                </label>
              ) : (
                <input
                  data-testid={`field-${key}`}
                  type={def.type === 'integer' || def.type === 'number' ? 'number' : 'text'}
                  value={params[key] ?? ''}
                  onChange={(e) => updateParam(key, def.type === 'integer' || def.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
                  placeholder={def.placeholder || ''}
                  step={def.step || (def.type === 'number' ? '0.001' : undefined)}
                  min={def.min}
                  max={def.max}
                  maxLength={def.max_length}
                  required={def.required}
                  style={fieldStyle}
                />
              )}
            </div>
          ))}

          <button
            data-testid="create-draft-btn"
            type="submit"
            disabled={submitting}
            style={{
              padding: '14px',
              background: theme.colors.primary,
              color: '#FFFFFF',
              border: 'none',
              borderRadius: theme.radius.sm,
              fontSize: '15px',
              fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.7 : 1,
              marginTop: '4px'
            }}
          >
            {submitting ? 'Generating...' : 'Generate Draft'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContractDraftForm;
